import { Link } from "react-router-dom";
import { MapPin, Clock, AlertTriangle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaSection } from "@/components/CtaSection";
import { FaqSection } from "@/components/FaqSection";
import { Testimonials } from "@/components/Testimonials";
import { SERVICES } from "@/data/services";
import { BAIRROS } from "@/data/bairros";
import { bairroUrl, comboUrl } from "@/lib/routes";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

const bairroFaq = (b) => [
  { q: `Vocês atendem ${b.prep} ${b.nome} 24 horas?`, a: `Sim, temos equipe de plantão 24 horas por dia atendendo ${b.prep} ${b.nome} e região, inclusive fins de semana e feriados. O tempo médio de chegada é de ${b.tempoChegada}.` },
  { q: `Quanto tempo a equipe demora para chegar ${b.prep} ${b.nome}?`, a: `Nosso tempo médio de chegada ${b.prep} ${b.nome} é de ${b.tempoChegada}, graças às equipes posicionadas estrategicamente em Guarulhos.` },
  { q: `Quais serviços vocês fazem ${b.prep} ${b.nome}?`, a: `Realizamos desentupimento de esgoto, pia, vaso sanitário e ralo, limpeza de fossa, hidrojateamento e limpeza de caixa de gordura ${b.prep} ${b.nome}, para residências, comércios e condomínios.` },
  { q: `O orçamento ${b.prep} ${b.nome} é gratuito?`, a: `Sim, o orçamento é totalmente gratuito e sem compromisso. Chame no WhatsApp (11) 97195-0197, descreva o problema e receba o valor fechado antes da visita.` },
];

export default function BairroPage({ bairro }) {
  const url = bairroUrl(bairro);
  const faq = bairroFaq(bairro);
  const crumbs = [
    { name: "Início", url: "/" },
    { name: "Bairros", url: "/bairros-atendidos" },
    { name: bairro.nome, url },
  ];
  const vizinhos = BAIRROS.filter((b) => b.slug !== bairro.slug).slice(0, 4);
  const depoimentos = bairro.depoimentos.map((d) => ({ ...d, bairro: bairro.nome }));

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-8" data-testid="bairro-page">
      <Seo
        title={`Desentupidora ${bairro.prep} ${bairro.nome} 24h | Chegada em ${bairro.tempoChegada.split(" a ")[0]} min`}
        description={`Desentupidora ${bairro.prep} ${bairro.nome}, Guarulhos, com atendimento 24 horas. Desentupimento de esgoto, pia, vaso e ralo, limpeza de fossa e hidrojateamento. Chegada em ${bairro.tempoChegada}. Ligue (11) 97195-0197.`}
        path={url}
        jsonLd={[localBusinessSchema(bairro.nome), faqSchema(faq), breadcrumbSchema(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      <section className="py-6">
        <span className="inline-flex items-center gap-2 bg-brand-yellow text-brand-dark text-xs font-heading font-black uppercase px-3 py-1.5 rounded-full mb-4">
          <Clock size={14} /> Chegada em {bairro.tempoChegada}
        </span>
        <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-dark uppercase tracking-tight leading-tight max-w-4xl">
          Desentupidora {bairro.prep} <span className="text-brand-red">{bairro.nome}</span> — 24 Horas
        </h1>
        <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed max-w-3xl">{bairro.descricao}</p>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-5 py-6" data-testid="bairro-info-cards">
        <div className="bg-brand-light rounded-xl p-5">
          <h2 className="font-heading font-bold text-base text-brand-dark flex items-center gap-2 mb-3"><MapPin size={17} className="text-brand-red" /> Ruas e avenidas atendidas</h2>
          <ul className="text-sm text-gray-600 space-y-1.5">{bairro.ruas.map((r) => <li key={r}>• {r}</li>)}</ul>
        </div>
        <div className="bg-brand-light rounded-xl p-5">
          <h2 className="font-heading font-bold text-base text-brand-dark flex items-center gap-2 mb-3"><MapPin size={17} className="text-brand-red" /> Pontos de referência</h2>
          <ul className="text-sm text-gray-600 space-y-1.5">{bairro.referencias.map((r) => <li key={r}>• {r}</li>)}</ul>
        </div>
        <div className="bg-brand-light rounded-xl p-5">
          <h2 className="font-heading font-bold text-base text-brand-dark flex items-center gap-2 mb-3"><AlertTriangle size={17} className="text-brand-red" /> Problemas comuns no bairro</h2>
          <ul className="text-sm text-gray-600 space-y-1.5">{bairro.problemas.map((r) => <li key={r}>• {r}</li>)}</ul>
        </div>
      </section>

      <section className="py-8" data-testid="servicos-bairro-section">
        <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-dark uppercase tracking-tight mb-3">Serviços de desentupimento {bairro.prep} {bairro.nome}</h2>
        <p className="text-gray-600 text-sm md:text-base mb-6 max-w-3xl">
          Todos os nossos serviços estão disponíveis {bairro.prep} {bairro.nome} com atendimento 24 horas, garantia formal e nota fiscal. Clique no serviço para ver a página completa do seu bairro:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={comboUrl(s, bairro)} className="bg-white border border-gray-100 rounded-lg px-4 py-3 shadow-sm hover:shadow-md hover:border-brand-red/40 text-sm font-medium text-brand-dark hover:text-brand-red transition-all" data-testid={`bairro-combo-${s.slug}`}>
              {s.nome} {bairro.prep} {bairro.nome}
            </Link>
          ))}
        </div>
      </section>

      <Testimonials depoimentos={depoimentos} titulo={`Avaliações de clientes ${bairro.prep} ${bairro.nome}`} />

      <FaqSection faq={faq} titulo={`Dúvidas sobre desentupimento ${bairro.prep} ${bairro.nome}`} />

      <section className="py-6" data-testid="bairros-vizinhos-section">
        <h2 className="font-heading font-bold text-xl text-brand-dark uppercase tracking-tight mb-4">Também atendemos perto de você</h2>
        <div className="flex flex-wrap gap-3">
          {vizinhos.map((b) => (
            <Link key={b.slug} to={bairroUrl(b)} className="bg-brand-light hover:bg-brand-red hover:text-white text-brand-dark text-sm font-medium px-4 py-2 rounded-full transition-colors" data-testid={`vizinho-${b.slug}`}>
              Desentupidora {b.prep} {b.nome}
            </Link>
          ))}
          <Link to="/" className="bg-brand-light hover:bg-brand-red hover:text-white text-brand-dark text-sm font-medium px-4 py-2 rounded-full transition-colors" data-testid="link-pagina-mae">
            Desentupidora em Guarulhos
          </Link>
        </div>
      </section>

      <CtaSection
        titulo={`Emergência ${bairro.prep} ${bairro.nome}?`}
        texto={`Nossa equipe chega em ${bairro.tempoChegada} no seu endereço. Orçamento gratuito e preço fechado pelo WhatsApp.`}
        waMsg={`Olá! Preciso de um desentupimento ${bairro.prep} ${bairro.nome}, Guarulhos. Pode me ajudar?`}
      />
    </main>
  );
}
